import { encodeFunctionData, parseAbi } from "viem"
import { z } from "zod"
import {
  type GachaEvmChain,
  gachaEvmAddressSchema,
  getGachaEvmChains,
  getGachaPreferredToken,
} from "./evm-chains"

export const gachaEthPaymentChainIdSchema = z
  .number()
  .int()
  .positive()
  .refine(
    (chainId) =>
      [
        ...getGachaEvmChains("production"),
        ...getGachaEvmChains("development"),
      ].some((chain) => chain.chainId === chainId),
    "ETH payment chain is not configured",
  )
export function getGachaEthPaymentDeployment(chainId: number) {
  const chain = getGachaEthPaymentChain(chainId)
  if (!chain.ethPayment)
    throw new Error("ETH payments are unavailable on this network")
  return {
    chainId: chain.chainId,
    ...chain.ethPayment,
    paymentToken: getGachaPreferredToken(chain).address,
  }
}
export function getGachaEthPaymentChain(chainId: number) {
  const chain = [
    ...getGachaEvmChains("production"),
    ...getGachaEvmChains("development"),
  ].find((chain) => chain.chainId === chainId)
  if (!chain) throw new Error("ETH payment chain is not configured")
  return chain
}
export const GACHA_ETH_SLIPPAGE_BPS = 50
export const gachaEthFeeTiers = [100, 500, 3000, 10000] satisfies number[]
const amount = z.string().regex(/^[1-9][0-9]{0,23}$/)
export const gachaEthPaymentInputSchema = z
  .object({
    chainId: gachaEthPaymentChainIdSchema,
    walletAddress: gachaEvmAddressSchema,
    requiredAmountBase: amount,
  })
  .strict()
export const gachaEthPaymentQuoteSchema = gachaEthPaymentInputSchema
  .extend({
    outputAmountBase: amount,
    estimatedInputAmountWei: amount,
    maxInputAmountWei: amount,
    estimatedGasWei: amount,
    fee: z.union([
      z.literal(100),
      z.literal(500),
      z.literal(3000),
      z.literal(10000),
    ]),
    deadline: z.number().int().positive(),
  })
  .strict()
export const gachaAuthenticatedEthPaymentQuoteSchema = z
  .object({ userId: z.string().min(1), quote: gachaEthPaymentQuoteSchema })
  .strict()
export type GachaEthPaymentInput = z.infer<typeof gachaEthPaymentInputSchema>
export type GachaEthPaymentQuote = z.infer<typeof gachaEthPaymentQuoteSchema>
export function supportsGachaEthPayment(
  chain: GachaEvmChain | null | undefined,
) {
  return Boolean(chain?.ethPayment)
}
export function getGachaEthOutputAmount(required: bigint) {
  return required
}
export function getGachaEthInputCap(estimate: bigint) {
  return (
    (estimate * BigInt(10000 + GACHA_ETH_SLIPPAGE_BPS) + BigInt(9999)) /
    BigInt(10000)
  )
}
export function validateGachaEthPaymentQuote(
  quote: GachaEthPaymentQuote,
  input: GachaEthPaymentInput,
  now = Date.now(),
) {
  if (
    quote.chainId !== input.chainId ||
    quote.walletAddress.toLowerCase() !== input.walletAddress.toLowerCase() ||
    quote.requiredAmountBase !== input.requiredAmountBase
  )
    throw new Error(
      "ETH payment wallet, network or pack total changed. Request a new quote.",
    )
  if (
    BigInt(quote.outputAmountBase) !==
      getGachaEthOutputAmount(BigInt(input.requiredAmountBase)) ||
    BigInt(quote.maxInputAmountWei) !==
      getGachaEthInputCap(BigInt(quote.estimatedInputAmountWei))
  )
    throw new Error(
      "ETH payment quote does not cover the approved pack total and spending cap",
    )
  if (
    quote.deadline <= Math.floor(now / 1000) ||
    quote.deadline > Math.floor(now / 1000) + 120
  )
    throw new Error("ETH payment quote expired. Request a new quote.")
  return quote
}
export const gachaEthRouterAbi = parseAbi([
  "function exactOutputSingle((address tokenIn,address tokenOut,uint24 fee,address recipient,uint256 amountOut,uint256 amountInMaximum,uint160 sqrtPriceLimitX96) params) payable returns (uint256 amountIn)",
  "function multicall(uint256 deadline,bytes[] data) payable returns (bytes[] results)",
  "function refundETH() payable",
])
export const gachaEthQuoterAbi = parseAbi([
  "function quoteExactOutputSingle((address tokenIn,address tokenOut,uint256 amount,uint24 fee,uint160 sqrtPriceLimitX96) params) returns (uint256 amountIn,uint160 sqrtPriceX96After,uint32 initializedTicksCrossed,uint256 gasEstimate)",
])
export function encodeGachaEthPayment(quote: GachaEthPaymentQuote) {
  const deployment = getGachaEthPaymentDeployment(quote.chainId)
  const swap = encodeFunctionData({
    abi: gachaEthRouterAbi,
    functionName: "exactOutputSingle",
    args: [
      {
        tokenIn: deployment.weth,
        tokenOut: deployment.paymentToken,
        fee: quote.fee,
        recipient: quote.walletAddress,
        amountOut: BigInt(quote.outputAmountBase),
        amountInMaximum: BigInt(quote.maxInputAmountWei),
        sqrtPriceLimitX96: BigInt(0),
      },
    ],
  })
  return encodeFunctionData({
    abi: gachaEthRouterAbi,
    functionName: "multicall",
    args: [
      BigInt(quote.deadline),
      [
        swap,
        encodeFunctionData({
          abi: gachaEthRouterAbi,
          functionName: "refundETH",
        }),
      ],
    ],
  })
}
